import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const emptyItem = { itemName: '', quantity: '', unit: 'pcs' };

const DirectDeliveryForm = () => {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    supplier: '', clientId: '', deliveredToType: 'agent',
    deliveredToName: '', deliveredToContact: '',
    deliveredToAddress: '', expectedDeliveryDate: '',
  });
  const [items, setItems]   = useState([{ ...emptyItem }]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/suppliers').then((r) => setSuppliers(r.data.data)).catch(console.error);
    api.get('/clients').then((r) => setClients(r.data.data)).catch(console.error);
  }, []);

  const updateItem = (i, field, value) => {
    const u = [...items];
    u[i] = { ...u[i], [field]: value };
    setItems(u);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.clientId) return toast.error('Please select the client (goods owner)');
    setLoading(true);
    try {
      await api.post('/direct-delivery', {
        supplier: form.supplier,
        clientId: form.clientId,
        deliveredTo: {
          type: form.deliveredToType,
          name: form.deliveredToName,
          contact: form.deliveredToContact,
          address: form.deliveredToAddress,
        },
        items: items.map((i) => ({ ...i, quantity: Number(i.quantity) })),
        expectedDeliveryDate: form.expectedDeliveryDate || undefined,
      });
      toast.success('Direct delivery created!');
      navigate('/direct-delivery');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create delivery');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-5">
      {/* Delivery details */}
      <div className="zip-card p-6 space-y-4">
        <h3 className="font-semibold text-zip-text tracking-tight">Delivery Details</h3>
        <div>
          <label className="block text-xs font-medium text-zip-muted mb-1.5">Client (Goods Owner) *</label>
          <select
            className="zip-input"
            value={form.clientId}
            onChange={(e) => setForm({ ...form, clientId: e.target.value })}
          >
            <option value="">Select Client</option>
            {clients.map((c) => <option key={c._id} value={c._id}>{c.name} ({c.code})</option>)}
          </select>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-zip-muted mb-1.5">Supplier *</label>
            <select
              className="zip-input"
              required
              value={form.supplier}
              onChange={(e) => setForm({ ...form, supplier: e.target.value })}
            >
              <option value="">Select Supplier</option>
              {suppliers.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-zip-muted mb-1.5">Expected Delivery Date</label>
            <input
              type="date"
              className="zip-input"
              value={form.expectedDeliveryDate}
              onChange={(e) => setForm({ ...form, expectedDeliveryDate: e.target.value })}
            />
          </div>
        </div>
      </div>

      {/* Delivered to */}
      <div className="zip-card p-6 space-y-4">
        <h3 className="font-semibold text-zip-text tracking-tight">Delivered To</h3>
        <div className="flex gap-4">
          {['agent', 'client'].map((t) => (
            <label key={t} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="deliveredToType"
                value={t}
                checked={form.deliveredToType === t}
                onChange={() => setForm({ ...form, deliveredToType: t })}
                className="accent-zip-red"
              />
              <span className="text-sm capitalize text-zip-text font-medium">{t}</span>
            </label>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Name', key: 'deliveredToName', required: true },
            { label: 'Contact', key: 'deliveredToContact', required: false },
            { label: 'Address', key: 'deliveredToAddress', required: false },
          ].map(({ label, key, required }) => (
            <div key={key}>
              <label className="block text-xs text-zip-muted mb-1">{label}{required ? ' *' : ''}</label>
              <input
                type="text"
                className="zip-input text-sm"
                required={required}
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Items */}
      <div className="zip-card p-6 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-zip-text tracking-tight">Items</h3>
          <button
            type="button"
            onClick={() => setItems([...items, { ...emptyItem }])}
            className="btn-ghost flex items-center gap-1.5 text-xs"
          >
            <Plus size={13} /> Add Item
          </button>
        </div>
        {items.map((item, i) => (
          <div key={i} className="bg-zip-surface rounded-xl border border-zip-border p-4 grid grid-cols-3 gap-3 items-end">
            <div>
              <label className="block text-xs text-zip-muted mb-1">Item Name *</label>
              <input
                type="text"
                className="zip-input text-sm"
                required
                value={item.itemName}
                onChange={(e) => updateItem(i, 'itemName', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs text-zip-muted mb-1">Quantity *</label>
              <input
                type="number"
                min="1"
                className="zip-input text-sm"
                required
                value={item.quantity}
                onChange={(e) => updateItem(i, 'quantity', e.target.value)}
              />
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-xs text-zip-muted mb-1">Unit</label>
                <select
                  className="zip-input text-sm"
                  value={item.unit}
                  onChange={(e) => updateItem(i, 'unit', e.target.value)}
                >
                  {['pcs', 'kg', 'box', 'roll', 'ltr', 'mtr'].map((u) => (
                    <option key={u}>{u}</option>
                  ))}
                </select>
              </div>
              {items.length > 1 && (
                <button
                  type="button"
                  onClick={() => setItems(items.filter((_, idx) => idx !== i))}
                  className="text-zip-muted hover:text-zip-red transition-colors mb-0.5"
                >
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading}
          className="btn-primary flex items-center gap-2"
        >
          {loading ? (
            <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Creating…</>
          ) : 'Create Delivery'}
        </button>
        <button type="button" onClick={() => navigate('/direct-delivery')} className="btn-ghost">Cancel</button>
      </div>
    </div>
  );
};

export default DirectDeliveryForm;
